import { ParticipantKind, Room, RoomEvent, Track } from 'livekit-client';
import { debug } from '../lib/debug';
import { setMicEnabled as setCopilotMic } from './livekitRoom';

/**
 * A voice call with an interactive analyst — the stock research analyst or
 * the mutual fund research analyst — over LiveKit.
 *
 * Its OWN room, apart from the copilot's (api/livekitRoom.js): that module is
 * one long-lived room the copilot owns, and joining an analyst through it
 * would pull the copilot out of its room and pour the analyst's events into
 * the copilot's thread. One analyst call at a time; starting one mutes the
 * copilot's microphone so the reader is not heard in two rooms.
 *
 * Not a React thing, for the copilot's reason: the call is started from the
 * start card and carried on in the session screen it opens, so it must outlive
 * the component that started it. React reads it through `onAnalystCall` /
 * `analystCallSnapshot` (api/hooks/analystCall.js).
 *
 * WHAT ARRIVES
 *   text  lk.transcription  what the reader and the analyst said → `line` events
 *                           (interims shown as the live caption, finals logged)
 *   data  lk.transcription  the reader's speech as a packet       → the same
 *   data  agent_event       one JSON per reply: { reply, data: { analyst_events,
 *                           session_id }, actions }                 → `envelope`
 *   text  lk.chat           a typed reply, or an envelope as JSON  → `line` | `envelope`
 *   attr  lk.agent.state    initializing | idle | listening | thinking | speaking
 *   audio                   the analyst's voice, through a hidden element
 *
 * Every final line and envelope is kept in an ordered log with a sequence
 * number, so the session screen — which may mount after the room is up —
 * takes each one exactly once (`consumeAnalystCall`).
 */

const TOPIC_CHAT = 'lk.chat';
const TOPIC_TRANSCRIPTION = 'lk.transcription';
const TOPIC_AGENT_EVENT = 'agent_event';
const ATTR_SEGMENT_ID = 'lk.segment_id';
const ATTR_TRANSCRIPTION_FINAL = 'lk.transcription_final';
const ATTR_TRANSCRIBED_TRACK = 'lk.transcribed_track_id';
const ATTR_AGENT_STATE = 'lk.agent.state';
const CONNECT_TIMEOUT_MS = 20000;
const AUDIO_CONTAINER_ID = 'ki-analyst-audio';

const ROOM_OPTIONS = {
  adaptiveStream: true,
  dynacast: true,
  audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/* ── state ────────────────────────────────────────────────────────────────── */

const IDLE = {
  /** idle | connecting | connected | reconnecting | ended | failed */
  status: 'idle',
  /** Which analyst ('stock' | 'mf') and which session the call belongs to. */
  kind: null,
  sessionId: null,
  room: null,
  agentPresent: false,
  agentState: null,
  mic: false,
  micError: null,
  canPlayback: true,
  error: null,
  /** The words being said right now, before they are final. */
  caption: { user: '', agent: '' },
  /** Ordered final lines and envelopes: { seq, type: 'line' | 'envelope', … }. */
  log: [],
  /** The last `seq` the session screen has taken. */
  consumed: 0,
};

let state = IDLE;
let room = null;
let gen = 0;
let seq = 0;
const listeners = new Set();
let decoder = null;

function set(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => {
    try { fn(state); } catch (err) { debug('analyst call · listener failed', { message: err?.message }); }
  });
}

function log(entry) {
  seq += 1;
  set({ log: [...state.log, { seq, at: new Date().toISOString(), ...entry }].slice(-400) });
}

/** Subscribe to the call's state; returns the unsubscribe. */
export function onAnalystCall(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const analystCallSnapshot = () => state;

/** The session screen has taken every entry up to `upTo`. */
export function consumeAnalystCall(upTo) {
  if (upTo > state.consumed) set({ consumed: upTo });
}

/* ── helpers ──────────────────────────────────────────────────────────────── */

function isAgent(p, target) {
  if (!p || typeof p.identity !== 'string' || !p.identity) return false;
  const local = target?.localParticipant;
  if (p === local || (local?.identity && p.identity === local.identity)) return false;
  return Boolean(p.isAgent || p.kind === ParticipantKind.AGENT || p.identity.startsWith('agent-'));
}

function findAgent(target) {
  if (!target) return null;
  for (const p of target.remoteParticipants.values()) if (isAgent(p, target)) return p;
  return null;
}

const agentStateOf = (p) => {
  const v = p?.attributes?.[ATTR_AGENT_STATE];
  return ['initializing', 'idle', 'listening', 'thinking', 'speaking'].includes(v) ? v : null;
};

const parse = (raw) => {
  try { return JSON.parse(raw); } catch { return undefined; }
};
const isEnvelope = (v) => v && typeof v === 'object' && !Array.isArray(v) && ('reply' in v || 'data' in v || 'actions' in v);

function audioContainer(create) {
  let el = document.getElementById(AUDIO_CONTAINER_ID);
  if (!el && create) {
    el = document.createElement('div');
    el.id = AUDIO_CONTAINER_ID;
    el.style.display = 'none';
    document.body.appendChild(el);
  }
  return el;
}
const attached = new Map();

function attachAudio(target, g, track, participant) {
  if (g !== gen || !track || track.kind !== Track.Kind.Audio || !isAgent(participant, target) || attached.has(track)) return;
  try {
    const el = track.attach();
    audioContainer(true).appendChild(el);
    attached.set(track, el);
  } catch (err) {
    console.warn('[analyst call] could not attach the analyst’s audio', err?.message ?? err);
  }
}
function detachAudio(track) {
  const el = attached.get(track);
  if (!el) return;
  attached.delete(track);
  try { track.detach(el); } catch { /* already stopped */ }
  el.remove();
  if (!attached.size) audioContainer(false)?.remove();
}
const releaseAudio = () => Array.from(attached.keys()).forEach(detachAudio);

/* ── what arrives ─────────────────────────────────────────────────────────── */

function setCaption(role, text) {
  set({ caption: { ...state.caption, [role]: text } });
}
function finalLine(role, id, text) {
  setCaption(role, '');
  if (String(text || '').trim()) log({ type: 'line', role, id, text: String(text).trim() });
}

/**
 * One transcription stream. The analyst's words come as ONE delta stream per
 * segment; the reader's as a new full-text stream per interim result, sharing
 * `lk.segment_id`, with the final flag in the header (see livekitRoom.js).
 */
async function readTranscription(target, g, reader, from) {
  const info = reader?.info ?? {};
  const header = info.attributes ?? {};
  const self = target.localParticipant?.identity ?? null;
  const trackSid = header[ATTR_TRANSCRIBED_TRACK];
  const isUser = Boolean((from && self && from === self) || (trackSid && target.localParticipant?.trackPublications?.has(trackSid)));
  if (!isUser) {
    const sender = from ? target.remoteParticipants.get(from) : null;
    if (sender && !isAgent(sender, target)) return;
  }
  const role = isUser ? 'user' : 'agent';
  const id = String(header[ATTR_SEGMENT_ID] || info.id || `t-${Date.now()}`);
  let text = '';
  try {
    for await (const chunk of reader) {
      if (g !== gen) return;
      text += chunk;
      if (text.trim()) setCaption(role, text);
    }
  } catch (err) {
    debug('analyst call · transcription stream failed', { message: err?.message });
  }
  if (g !== gen) return;
  const flag = (reader?.info?.attributes ?? header)[ATTR_TRANSCRIPTION_FINAL];
  const final = isUser ? flag === undefined || flag === 'true' : true;
  if (final) finalLine(role, id, text);
}

/** A typed reply streamed on lk.chat — text, or an envelope as JSON. */
async function readChat(target, g, reader, from) {
  const self = target.localParticipant?.identity ?? null;
  if (from && self && from === self) return;
  let raw = '';
  try {
    for await (const chunk of reader) {
      if (g !== gen) return;
      raw += chunk;
    }
  } catch (err) {
    debug('analyst call · chat stream failed', { message: err?.message });
  }
  if (g !== gen || !raw.trim()) return;
  const parsed = raw.trim().startsWith('{') ? parse(raw.trim()) : undefined;
  if (isEnvelope(parsed)) log({ type: 'envelope', payload: parsed });
  else finalLine('agent', reader?.info?.id || `c-${Date.now()}`, parsed?.text ?? raw);
}

function handleData(target, payload, participant, topic) {
  if (participant && !isAgent(participant, target)) return;
  decoder = decoder || new TextDecoder();
  const parsed = parse(decoder.decode(payload));
  if (!parsed || typeof parsed !== 'object') return;
  if (topic === TOPIC_AGENT_EVENT || (!topic && isEnvelope(parsed))) {
    log({ type: 'envelope', payload: parsed });
    return;
  }
  if (topic === TOPIC_TRANSCRIPTION && parsed.role === 'user' && typeof parsed.transcript === 'string') {
    if (parsed.is_final === true) finalLine('user', `d-${Date.now()}`, parsed.transcript);
    else setCaption('user', parsed.transcript);
  }
}

function bind(target, g) {
  const on = (event, fn) => target.on(event, (...args) => { if (g === gen) fn(...args); });
  on(RoomEvent.Disconnected, () => {
    if (room !== target) return;
    room = null;
    releaseAudio();
    set({
      status: 'ended', agentPresent: false, agentState: null, mic: false, caption: { user: '', agent: '' },
    });
  });
  on(RoomEvent.Reconnecting, () => set({ status: 'reconnecting' }));
  on(RoomEvent.Reconnected, () => set({ status: 'connected', agentPresent: Boolean(findAgent(target)) }));
  on(RoomEvent.ParticipantConnected, (p) => { if (isAgent(p, target)) set({ agentPresent: true, agentState: agentStateOf(p) }); });
  on(RoomEvent.ParticipantDisconnected, () => { const a = findAgent(target); set({ agentPresent: Boolean(a), agentState: agentStateOf(a) }); });
  on(RoomEvent.ParticipantAttributesChanged, (_c, p) => { if (isAgent(p, target)) set({ agentState: agentStateOf(p) }); });
  on(RoomEvent.AudioPlaybackStatusChanged, (playing) => set({ canPlayback: Boolean(playing) }));
  on(RoomEvent.TrackSubscribed, (track, _pub, participant) => attachAudio(target, g, track, participant));
  on(RoomEvent.TrackUnsubscribed, (track) => detachAudio(track));
  on(RoomEvent.TrackMuted, (pub, p) => { if (p === target.localParticipant && pub?.source === Track.Source.Microphone) set({ mic: false }); });
  on(RoomEvent.TrackUnmuted, (pub, p) => { if (p === target.localParticipant && pub?.source === Track.Source.Microphone) set({ mic: true }); });
  on(RoomEvent.MediaDevicesError, (error) => set({ micError: error?.message || String(error) }));
  on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => handleData(target, payload, participant, topic));
  [[TOPIC_TRANSCRIPTION, readTranscription], [TOPIC_CHAT, readChat]].forEach(([topic, read]) => {
    try {
      target.registerTextStreamHandler(topic, (reader, participantInfo) => {
        if (g !== gen) return;
        read(target, g, reader, participantInfo?.identity ?? null).catch(() => {});
      });
    } catch (err) {
      console.warn(`[analyst call] could not listen on ${topic}`, err?.message ?? err);
    }
  });
}

/* ── the call ─────────────────────────────────────────────────────────────── */

/**
 * Join the analyst's room. `kind` and `sessionId` say whose call it is, so the
 * session screen on show takes only its own. Leaves any call already up.
 * Must start inside a click for the analyst's voice to play; where the browser
 * still refuses, `canPlayback` is false and `unlockAnalystAudio` (on a click)
 * lets it play.
 */
export async function startAnalystCall({ token, wsUrl, room: roomName, kind, sessionId }) {
  if (!token || !wsUrl) throw new Error('The voice service sent no room token.');
  await endAnalystCall({ quiet: true });
  gen += 1;
  const g = gen;
  seq = 0;
  const next = new Room(ROOM_OPTIONS);
  bind(next, g);
  set({
    ...IDLE, status: 'connecting', callId: g, kind, sessionId: Number(sessionId) || null, room: roomName || null,
  });
  let timer = null;
  try {
    await Promise.race([
      next.connect(wsUrl, token, { autoSubscribe: true }),
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('The voice room did not answer in time.')), CONNECT_TIMEOUT_MS); }),
    ]);
  } catch (err) {
    if (g === gen) set({ status: 'failed', error: err?.message || 'Could not join the voice room.' });
    next.disconnect().catch(() => {});
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (g !== gen) { next.disconnect().catch(() => {}); return; }
  room = next;
  const agent = findAgent(next);
  set({ status: 'connected', agentPresent: Boolean(agent), agentState: agentStateOf(agent), canPlayback: next.canPlaybackAudio });
  /* Heard in one room only. */
  setCopilotMic(false).catch(() => {});
  try { await next.startAudio(); } catch { /* unlocked later from a click */ }
  set({ canPlayback: next.canPlaybackAudio });
  await setAnalystMic(true);
}

/** Leave the call. The session, its revisions and its chat stay. */
export async function endAnalystCall({ quiet = false } = {}) {
  const target = room;
  gen += 1;
  room = null;
  releaseAudio();
  if (target) await target.disconnect().catch(() => {});
  if (!quiet) set({ status: 'ended', agentPresent: false, agentState: null, mic: false, caption: { user: '', agent: '' } });
}

export async function setAnalystMic(on) {
  if (!room) return false;
  try {
    await room.localParticipant.setMicrophoneEnabled(Boolean(on));
    set({ mic: Boolean(room.localParticipant.isMicrophoneEnabled), micError: null });
    return true;
  } catch (err) {
    set({ mic: false, micError: err?.name === 'NotAllowedError' ? 'Microphone blocked — allow it in the browser’s site settings.' : (err?.message || 'The microphone could not start.') });
    return false;
  }
}

/** A typed line into the call — the analyst answers it as it would speech. */
export async function sendAnalystText(text) {
  if (!room) throw new Error('The voice call is not connected.');
  await room.localParticipant.sendText(JSON.stringify({ text: String(text) }), { topic: TOPIC_CHAT });
  log({ type: 'line', role: 'user', id: `typed-${Date.now()}`, text: String(text), typed: true });
}

/*
 * The session screen holds the call while it is on show; when the last holder
 * lets go the call ends — after a beat, so a remount (React's StrictMode, a
 * re-key) that takes hold again at once does not hang up.
 */
let holders = 0;
export function holdAnalystCall() {
  holders += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    setTimeout(() => {
      if (holders === 0 && room) endAnalystCall().catch(() => {});
    }, 400);
  };
}

export async function unlockAnalystAudio() {
  if (!room) return false;
  try { await room.startAudio(); } catch { /* still refused */ }
  set({ canPlayback: room?.canPlaybackAudio ?? false });
  return state.canPlayback;
}
